/**
 * CSV export for counter tiles whose headline metric is `uniqExact`.
 *
 * The counter query itself returns a single aggregated number. The export
 * re-runs the same filters / time range grouped by that uniqueness field so
 * the CSV has one row per unique entity (user id or email).
 */

import apiClient from 'utils/ApiClient';
import {
  FilterDef,
  MetricDef,
  ReportQueryDSL,
  TimeRangeDef,
} from '../Settings/reportDsl';
import {
  buildReportTableCsv,
  CsvColumn,
  downloadCsvFile,
  REPORT_TABLE_EXPORT_MAX_ROWS,
  sanitizeCsvFilename,
} from './exportReportTableCsv';

const EXPORT_PAGE_SIZE = 500;

const identityColumnLabel = (field: string): string => {
  const last = field.split('.').pop() || field;
  if (last === 'user_id') return 'USER ID';
  if (last === 'user_email') return 'EMAIL';
  return last.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const companionIdentity = (uniqField: string): { field: string; label: string } | null => {
  if (uniqField === 'user_id') {
    return { field: 'custom_dimensions.user_email', label: 'EMAIL' };
  }
  if (uniqField === 'custom_dimensions.user_email' || uniqField === 'user_email') {
    return { field: 'user_id', label: 'USER ID' };
  }
  return null;
};

const eventCountLabel = (uniqField: string): string => {
  if (uniqField === 'user_id') return 'Login Event';
  if (uniqField.endsWith('user_email')) return 'Total Login Event';
  return 'Event Count';
};

const uniqExactField = (query: ReportQueryDSL | undefined): string | null => {
  const metric = query?.metrics?.[0];
  if (!metric || metric.fn !== 'uniqExact' || !metric.field) return null;
  return metric.field;
};

/**
 * Build a grouped export query for a `uniqExact` counter, or `null` when the
 * counter is a simple count / sum and has no unique-entity breakdown.
 */
export const buildUniqCounterExportQuery = (
  query: ReportQueryDSL | undefined,
): ReportQueryDSL | null => {
  if (!query) return null;
  const uniqField = uniqExactField(query);
  if (!uniqField) return null;

  const companion = companionIdentity(uniqField);
  const metrics: MetricDef[] = [
    ...(companion
      ? [{ field: companion.field, fn: 'argMax' as const, label: companion.label }]
      : []),
    { field: 'event_time', fn: 'max', label: 'Last Login' },
    { field: '*', fn: 'count', label: eventCountLabel(uniqField) },
  ];
  const lastLoginMetricIndex = companion ? 1 : 0;

  return {
    ...query,
    dimensions: [{ field: uniqField, label: identityColumnLabel(uniqField) }],
    metrics,
    orderBy: [{ field: `metric:${lastLoginMetricIndex}`, dir: 'DESC' }],
    limit: REPORT_TABLE_EXPORT_MAX_ROWS,
    offset: undefined,
    timeRange: query.timeRange
      ? { ...query.timeRange, granularity: undefined }
      : query.timeRange,
  };
};

export const csvColumnsFromExportQuery = (query: ReportQueryDSL): CsvColumn[] => {
  const dims = (query.dimensions || []).map((d, i) => ({
    key: `dim_${i}`,
    label: d.label || `dim_${i}`,
  }));
  const metrics = (query.metrics || []).map((m, i) => ({
    key: `metric_${i}`,
    label: m.label || `metric_${i}`,
  }));
  return [...dims, ...metrics];
};

export interface UniqCounterCsvExportResult {
  empty: boolean;
  truncated: boolean;
  exported: number;
  totalHint?: number;
}

const fetchExportRows = async (opts: {
  tenantId: string;
  pathSite: string;
  query: ReportQueryDSL;
  runtimeFilters?: FilterDef[];
  timeRangeOverride?: TimeRangeDef;
  timezone?: string;
}): Promise<{ rows: Record<string, unknown>[]; truncated: boolean; totalHint?: number }> => {
  const maxRows = REPORT_TABLE_EXPORT_MAX_ROWS;
  const allRows: Record<string, unknown>[] = [];
  let pageIndex = 0;
  let totalHint: number | undefined;

  while (allRows.length < maxRows) {
    const body: Record<string, unknown> = {
      query: { ...opts.query, limit: maxRows },
      runtimeFilters: opts.runtimeFilters || [],
      timeRangeOverride: opts.timeRangeOverride,
      pagination: { page: pageIndex, pageSize: EXPORT_PAGE_SIZE },
    };
    if (opts.timezone) body.timezone = opts.timezone;

    const res: {
      data?: Record<string, unknown>[];
      totalCount?: number;
    } = await apiClient.post(
      `/il/analytics/${opts.tenantId}/${opts.pathSite}/report-query`,
      body,
    );

    if (typeof res?.totalCount === 'number') totalHint = res.totalCount;
    const batch = res?.data ?? [];
    if (batch.length === 0) break;
    allRows.push(...batch);
    if (batch.length < EXPORT_PAGE_SIZE) break;
    if (allRows.length >= maxRows) break;
    pageIndex += 1;
  }

  const rows = allRows.slice(0, maxRows);
  const truncated = typeof totalHint === 'number'
    ? totalHint > maxRows
    : rows.length >= maxRows;
  return { rows, truncated, totalHint };
};

export const downloadUniqCounterCsv = async (opts: {
  tenantId: string;
  pathSite: string;
  exportQuery: ReportQueryDSL;
  runtimeFilters?: FilterDef[];
  timeRangeOverride?: TimeRangeDef;
  timezone?: string;
  filenameTitle: string;
}): Promise<UniqCounterCsvExportResult> => {
  const { rows, truncated, totalHint } = await fetchExportRows({
    tenantId: opts.tenantId,
    pathSite: opts.pathSite,
    query: opts.exportQuery,
    runtimeFilters: opts.runtimeFilters,
    timeRangeOverride: opts.timeRangeOverride,
    timezone: opts.timezone,
  });

  if (rows.length === 0) {
    return { empty: true, truncated: false, exported: 0, totalHint };
  }

  const csv = buildReportTableCsv(csvColumnsFromExportQuery(opts.exportQuery), rows);
  downloadCsvFile(csv, sanitizeCsvFilename(opts.filenameTitle));
  return { empty: false, truncated, exported: rows.length, totalHint };
};
